// ============================================================================
// AM Today's Route — the AM's opening screen (handoff §3A). Mobile-first,
// one-handed. Shows today's due stops from v_am_routes_today, a tappable A↔B
// parity chip, per-stop visit/skip/reorder (day layer), an ad-hoc add, and a
// collapsed "As able" section from v_am_routes_week.
//
// All edits write the DAY layer (am_route_day_instances / _day_stops) — the
// recurring template is never touched here (that's the template editor).
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarDays, Plus, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  RagDot,
  Skeleton,
  useToast,
} from '../../shared/components';
import {
  CADENCE_LABELS,
  useFacilityOptions,
  useTodayRoute,
  useWeekRoute,
  useWeekState,
  type ResolvedStop,
  type RouteParity,
} from './hooks/use-am-routes-data';
import StopCard from './components/stop-card';
import SkipModal from './components/skip-modal';
import AdHocModal from './components/ad-hoc-modal';
import WeekView from './components/week-view';
import { nextWeekTarget, thisWeekTarget } from './lib/route-week-math';
import { errorMessage } from '../../shared/errors';

type RouteTab = 'today' | 'this' | 'next';

// R-1 return state (AFB-0063): tapping a facility saves the active tab +
// scroll position so Back lands on the same view, same day, same spot.
const RETURN_KEY = 'amRoutes.return';

function savedReturnTab(): RouteTab | null {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    const t = (JSON.parse(raw) as { tab?: string }).tab;
    return t === 'today' || t === 'this' || t === 'next' ? t : null;
  } catch {
    return null;
  }
}

/**
 * "N paused — not shown."
 *
 * Ben's standing rule is that nothing disappears from a screen; his preference
 * is that paused facilities stay off the driving list. This line is how both
 * hold at once. It also states that the reversal is automatic, because the
 * question an AM will ask is "will it come back".
 */
function PausedHiddenNote({ count, scope }: { count: number; scope: string }) {
  if (count <= 0) return null;
  return (
    <p className="mt-3 rounded-lg border border-st-info-border bg-st-info-bg px-3 py-2 text-xs text-st-info-fg">
      <strong>
        {count} paused {count === 1 ? 'facility is' : 'facilities are'} not shown
      </strong>{' '}
      for {scope}. Service is on hold there, so the stop is off the route — the
      rotation is untouched and it returns by itself when service resumes.
    </p>
  );
}

export default function AMTodayRoute() {
  const { employee, profile } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const myEid = employee?.employee_id ?? profile?.employee_id ?? null;

  const route = useTodayRoute(myEid, myEid);
  const week = useWeekRoute(myEid, { withContacts: true });
  const { state: weekState } = useWeekState(myEid);
  const { options: facilityOptions } = useFacilityOptions();

  const [tab, setTab] = useState<RouteTab>(() => savedReturnTab() ?? 'today');
  const rootRef = useRef<HTMLDivElement>(null);
  const restoredRef = useRef(false);

  // Open the facility's account page. Guarded on facility_id — non-facility
  // stops (office etc.) have no target. A missing/closed facility still
  // navigates; the account page renders its own not-found state.
  function openFacility(facilityId: string | null | undefined) {
    if (!facilityId) return;
    try {
      const scroller = rootRef.current?.closest('main');
      sessionStorage.setItem(
        RETURN_KEY,
        JSON.stringify({ tab, scroll: scroller?.scrollTop ?? 0 }),
      );
    } catch {
      // sessionStorage unavailable — navigation still works, restore is best-effort.
    }
    navigate(`/account-management/facilities/${facilityId}`);
  }

  // Restore scroll once data has rendered, then consume the saved state.
  const listsLoading = route.isLoading || week.isLoading;
  useEffect(() => {
    if (restoredRef.current || listsLoading) return;
    restoredRef.current = true;
    try {
      const raw = sessionStorage.getItem(RETURN_KEY);
      if (!raw) return;
      sessionStorage.removeItem(RETURN_KEY);
      const saved = JSON.parse(raw) as { scroll?: number };
      const scroller = rootRef.current?.closest('main');
      if (scroller && typeof saved.scroll === 'number') {
        scroller.scrollTop = saved.scroll;
      }
    } catch {
      // best-effort only
    }
  }, [listsLoading]);
  const [pending, setPending] = useState(false);
  const [skipTarget, setSkipTarget] = useState<ResolvedStop | null>(null);
  const [showAdHoc, setShowAdHoc] = useState(false);
  const [showAsAble, setShowAsAble] = useState(false);

  const asAble = useMemo(
    () => week.rows.filter((r) => r.is_as_able),
    [week.rows],
  );

  const thisTarget = useMemo(() => thisWeekTarget(weekState), [weekState]);
  const nextTarget = useMemo(() => nextWeekTarget(weekState), [weekState]);

  const today = new Date();
  const dateLabel = today.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  const activeCount = route.stops.filter(
    (s) => s.status === 'Planned' || s.status === 'Visited',
  ).length;
  const visitedCount = route.stops.filter((s) => s.status === 'Visited').length;
  // Paused facilities are now REMOVED from today's list (Ben, 2026-08-21), so
  // this count comes from the hook rather than from the rendered stops — which
  // no longer contain any. It is displayed because a filtered list must never
  // read as a shorter one: his standing rule is that nothing disappears from a
  // screen, and "1 paused — not shown" satisfies both that and the preference
  // to keep paused sites off the driving list.
  const pausedCount = route.pausedHidden;

  async function run(fn: () => Promise<void>, okMsg?: string) {
    setPending(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(errorMessage(e, 'Something went wrong.'));
    } finally {
      setPending(false);
    }
  }

  async function toggleParity() {
    const next: RouteParity = route.parity === 'A' ? 'B' : 'A';
    await run(() => route.flipParity(next), `Week set to ${next}`);
  }

  function move(stop: ResolvedStop, dir: 'up' | 'down') {
    const ordered = [...route.stops];
    const i = ordered.findIndex((s) => s.key === stop.key);
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= ordered.length) return;
    [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
    void run(() => route.reorder(ordered.map((s) => s.key)));
  }

  return (
    <div ref={rootRef} className="mx-auto max-w-2xl p-4 pb-28">
      <PageHeader
        title="Today's route"
        meta={
          <span className="flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            {dateLabel}
            {!route.isLoading &&
              ` · ${activeCount} stop${activeCount === 1 ? '' : 's'}` +
                (visitedCount ? ` · ${visitedCount} done` : '')}
            {!route.isLoading && pausedCount > 0 && (
              <span
                className="rounded-full border border-st-info-border bg-st-info-bg px-2 py-0.5 text-[11px] font-semibold text-st-info-fg-strong"
                title="Paused facilities are left off today's route. They come back automatically when service resumes — the rotation is untouched."
              >
                {pausedCount} paused — not shown
              </span>
            )}
          </span>
        }
        actions={
          <>
            <ParityChip
              parity={route.parity}
              disabled={pending || route.isLoading}
              onToggle={toggleParity}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={route.refetch}
              disabled={route.isLoading}
              icon={<RefreshCw className="h-3.5 w-3.5" />}
            >
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </>
        }
      />

      <RouteTabs tab={tab} onChange={setTab} />

      {tab === 'today' && (
      <>
      <div className="mt-3 flex items-center justify-between">
        <Link
          to="/route/template"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-tcs-text-secondary hover:text-tcs-text"
        >
          <SlidersHorizontal className="h-4 w-4" /> Edit my rotation
        </Link>
        {route.isMaterialized && (
          <span className="text-[11px] font-medium text-tcs-text-muted">
            Day edited · template unchanged
          </span>
        )}
      </div>

      {route.error && (
        <div className="mt-4">
          <ErrorState body={route.error} onRetry={route.refetch} />
        </div>
      )}

      {route.isLoading && (
        <div className="mt-4 space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} block height={96} className="w-full rounded-xl" />
          ))}
        </div>
      )}

      {!route.isLoading && !route.error && route.stops.length === 0 && (
        <div className="mt-4">
          <EmptyState
            title="No scheduled stops today"
            body="Nothing on your rotation for today. Add an ad-hoc stop or check your as-able list below."
          />
        </div>
      )}

      {!route.isLoading && route.stops.length > 0 && (
        <div className="mt-4 space-y-3">
          {route.stops.map((stop, i) => (
            <StopCard
              key={stop.key}
              stop={stop}
              index={i}
              total={route.stops.length}
              busy={pending}
              onVisit={() =>
                run(
                  () =>
                    route.visitStop({
                      dayStopId: stop.dayStopId,
                      assignmentId: stop.assignmentId,
                    }),
                  'Marked visited',
                )
              }
              onSkip={() => setSkipTarget(stop)}
              onReset={() =>
                run(() =>
                  route.resetStop({
                    dayStopId: stop.dayStopId,
                    assignmentId: stop.assignmentId,
                  }),
                )
              }
              onMove={(dir) => move(stop, dir)}
              onOpen={
                stop.facilityId
                  ? () => openFacility(stop.facilityId)
                  : undefined
              }
            />
          ))}
        </div>
      )}
      </>
      )}

      {tab === 'this' && (
        <>
          <PausedHiddenNote count={week.pausedHidden} scope="this week" />
          <WeekView
            rows={week.rows}
            target={thisTarget}
            highlightToday
            onOpenFacility={openFacility}
          />
        </>
      )}
      {tab === 'next' && (
        <>
          <PausedHiddenNote count={week.pausedHidden} scope="next week" />
          <WeekView
            rows={week.rows}
            target={nextTarget}
            onOpenFacility={openFacility}
          />
        </>
      )}

      {/* As-able section — relevant to today + this-week planning */}
      {tab !== 'next' && asAble.length > 0 && (
        <div className="mt-5">
          <button
            type="button"
            onClick={() => setShowAsAble((v) => !v)}
            className="flex w-full items-center justify-between rounded-lg border border-tcs-border bg-tcs-bg px-3 py-2.5 text-sm font-medium text-tcs-text-secondary"
          >
            <span>As able · no fixed day ({asAble.length})</span>
            <span className="text-tcs-text-muted">{showAsAble ? 'Hide' : 'Show'}</span>
          </button>
          {showAsAble && (
            <ul className="mt-2 space-y-1.5">
              {asAble.map((r) => (
                <li
                  key={r.assignment_id}
                  onClick={
                    r.facility_id
                      ? () => openFacility(r.facility_id)
                      : undefined
                  }
                  {...(r.facility_id
                    ? {
                        role: 'link' as const,
                        tabIndex: 0,
                        'aria-label': `Open ${r.facility_name ?? r.stop_label ?? 'stop'}`,
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openFacility(r.facility_id);
                          }
                        },
                      }
                    : {})}
                  className={`flex items-center gap-2 rounded-lg border border-tcs-border bg-tcs-raised px-3 py-2 text-sm ${
                    r.facility_id
                      ? 'cursor-pointer transition-colors hover:bg-tcs-bg-muted/50 active:bg-tcs-bg-muted'
                      : ''
                  }`}
                >
                  <RagDot status={r.account_status_rag} />
                  <span className="min-w-0 flex-1 truncate text-tcs-text">
                    {r.facility_name ?? r.stop_label}
                    {(r.display_contact ?? r.poc_initials) && (
                      <span className="text-tcs-text-secondary">
                        {' '}
                        ({r.display_contact ?? r.poc_initials})
                      </span>
                    )}
                  </span>
                  {r.cadence && (
                    <span className="shrink-0 rounded-full bg-tcs-surface px-2 py-0.5 text-[11px] text-tcs-text-secondary">
                      {CADENCE_LABELS[r.cadence]}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Add ad-hoc FAB — Today only (writes the day layer) */}
      {tab === 'today' && (
        <button
          type="button"
          onClick={() => setShowAdHoc(true)}
          aria-label="Add a stop today"
          // AFB-0020 — clear the mobile bottom nav on narrow viewports (match
          // the Feedback pill's bottom-20 clearance); tuck back to the corner
          // on md+ where the sidebar replaces the bottom bar.
          className="fixed bottom-20 right-4 z-40 flex h-16 w-16 items-center justify-center rounded-full bg-tcs-inverse text-tcs-inverse-ink shadow-lg transition-colors hover:bg-tcs-inverse-hover active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-tcs-accent focus-visible:ring-offset-2 md:bottom-4 md:right-6"
        >
          <Plus className="h-7 w-7" strokeWidth={2.5} />
        </button>
      )}

      {skipTarget && (
        <SkipModal
          stopLabel={skipTarget.label}
          onClose={() => setSkipTarget(null)}
          onConfirm={async (reason) => {
            await route.skipStop(
              {
                dayStopId: skipTarget.dayStopId,
                assignmentId: skipTarget.assignmentId,
              },
              reason,
            );
            setSkipTarget(null);
            toast.success('Stop skipped for today');
          }}
        />
      )}

      {showAdHoc && (
        <AdHocModal
          facilityOptions={facilityOptions}
          onClose={() => setShowAdHoc(false)}
          onAdd={async (input) => {
            await route.addAdHoc(input);
            setShowAdHoc(false);
            toast.success('Stop added for today');
          }}
        />
      )}
    </div>
  );
}

function RouteTabs({
  tab,
  onChange,
}: {
  tab: RouteTab;
  onChange: (t: RouteTab) => void;
}) {
  const tabs: Array<{ id: RouteTab; label: string }> = [
    { id: 'today', label: 'Today' },
    { id: 'this', label: 'This Week' },
    { id: 'next', label: 'Next Week' },
  ];
  return (
    <div
      role="tablist"
      aria-label="Route view"
      className="mt-3 flex rounded-lg border border-tcs-border bg-tcs-bg p-0.5"
    >
      {tabs.map((t) => {
        const active = t.id === tab;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.id)}
            className={`h-9 flex-1 rounded-md text-sm font-medium transition-colors ${
              active
                ? 'bg-tcs-raised text-tcs-text shadow-sm'
                : 'text-tcs-text-secondary hover:text-tcs-text'
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function ParityChip({
  parity,
  disabled,
  onToggle,
}: {
  parity: RouteParity | null;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      title="Tap to flip this week's A/B rotation"
      className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-tcs-border-strong bg-tcs-bg px-3 text-xs font-medium text-tcs-text hover:bg-tcs-bg-muted disabled:opacity-50"
    >
      <span className="text-tcs-text-secondary">Week</span>
      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-tcs-inverse text-[11px] font-bold text-tcs-inverse-ink">
        {parity ?? '—'}
      </span>
    </button>
  );
}
